import React, { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"

import {
  Alert,
  AppBar,
  Button,
  DialogContent,
  DialogTitle,
  Drawer,
  IconButton,
  Snackbar,
  styled,
  TextField,
  Typography,
  useTheme
} from "@mui/material"

import {
  ArrowUpwardRounded,
  ChevronRightRounded,
  CloseRounded,
  HomeRounded,
  MenuRounded,
  SearchRounded
} from "@mui/icons-material"

import HKVDialog from "./components/HKVDialog/7.0"
import HKVIcon from "./components/HKVIcon/1.0"

import HKVLink from "./components/HKVLink"

import moment from "moment"
import "moment/min/locales"

import packageJson from "../package.json"
import "./App.css"

import { clearError, removeError } from "./store/reducers/errors"

import {
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom"

import {
  Contact,
  Disclaimer,
  Home,
  OverLVO,
  Privacyverklaring,
  NotFound,
  Thema,
  Zoeken
} from "./pages"

import DesktopMenu from "./components/StyledComponents/DesktopMenu"
import Footer from "./components/StyledComponents/Footer"
import Header from "./components/StyledComponents/Header"
import Menu from "./components/StyledComponents/Menu"

import HKVAnalytics from "./components/HKVAnalytics/1.0"
import ScrollToTop from "./components/ScrollToTop"

import { GetCategories } from "./store/reducers/functions"

import { capitalizeFirstLetter } from "./lib/utils"

const StyledMobileMenu = styled("div", {
})(({ theme }) => {
  return {
    display: "none",
    [theme.breakpoints.down("md")]: {
      display: "flex"
    }
  }
})

const StyledHeadMenu = styled("nav", {
})(({ theme }) => {
  return {
    display: "flex",
    justifyContent: "flex-end",
    position: "absolute",
    width: "100%",
    maxWidth: 1200,
    margin: `${theme.spacing(2)} auto`,
    left: 0,
    right: 0,
    "& .menu-list": {
      display: "flex",
      justifyContent: "flex-end",
      listStyle: "none",
      gap: theme.spacing(3),
      padding: 0
    },
    [theme.breakpoints.down("md")]: {
      display: "none"
    }
  }
})

function App() {
  const dispatch = useDispatch()

  const errors = useSelector((state) => state.errors)

  const [drawerStates, setDrawerStates] = useState({ left: false })
  const [searchValue, setSearchValue] = useState()

  const theme = useTheme()

  const navigate = useNavigate()

  const [categories, setCategories] = useState([])
  const [pages, setPages] = useState([
    { icon: "HomeRounded", label: "Home", path: "/" },
    { icon: "WaterRounded", label: "Over LVO", path: "/over-lvo" },
    { icon: "CategoryRounded", label: "Thema's", path: "/themas" },
    { label: "Contact", path: "/contact", headMenu: true },
    { label: "Disclaimer", path: "/disclaimer", headMenu: true }
  ])
  const [openPages, setOpenPages] = useState({})

  const [partners, setPartners] = useState([
    {
      title: "Naar de homepage van Unie van Waterschappen",
      alt: "Unie van Waterschappen",
      link: "https://unievanwaterschappen.nl",
      image: "/static/images/partners/uvw-198x40.png"
    },
    {
      title: "Naar de homepage van Interprovinciaal Overleg",
      alt: "Interprovinciaal Overleg",
      link: "https://www.ipo.nl",
      image: "/static/images/partners/IPO-logo.svg"
    }
  ])

  useEffect(() => {
    moment.locale("nl")

    const buildSubpages = (data, basePath = "/themas") => {
      return data.map(d => {
        const path = `${basePath}/${d.key}`
        return {
          label: d.name,
          path,
          subpages: d.themas ? buildSubpages(d.themas, path) : []
        }
      })
    }

    dispatch(GetCategories())
      .then((json) => {
        const data = Array.isArray(json) ? json : []

        setCategories(data)
        setPages(prevState => {
          let themePages = prevState.find(ps => ps.path === "/themas")
          if (!themePages) return [...prevState]

          themePages.subpages = buildSubpages(data)

          return [...prevState]
        })
      })
  }, [])

  const drawerStateChanged = (placement, state) => {
    setDrawerStates(prevState => {
      prevState[placement] = state
      return { ...prevState }
    })
  }

  const closeSnackbar = (reason, index) => {
    if (reason === "clickaway") return null

    dispatch(removeError({ index }))
  }

  const renderErrors = () => {
    const criticalErrors = errors.filter(error => error.type === "critical")

    return (
      <HKVDialog
        config={{ open: criticalErrors.length > 0, size: "md" }}
        onClose={() => dispatch(clearError())}
      >
        <DialogTitle>{criticalErrors.length > 1 ? "Er zijn meerdere fouten opgetreden" : "Er is een fout opgetreden"}</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", maxWidh: "100%", maxHeight: 400 }}>
          <div style={{ display: "flex", flexDirection: "column", overflow: "auto" }}>
            {criticalErrors.map((error, index) => (
              <div key={index} style={{ border: `1px solid ${theme.palette.grey[300]}`, borderRadius: theme.shape.borderRadius, padding: theme.spacing(0.5, 1), marginTop: index === 0 ? 0 : theme.spacing(0.5) }}>
                <div style={{ display: "flex" }}>
                  <Typography sx={{ marginRight: theme.spacing(1), width: 60 }}>Melding:</Typography>
                  <Typography><b>{error.message}</b></Typography>
                </div>
                <div style={{ display: "flex" }}>
                  <Typography sx={{ marginRight: theme.spacing(1), width: 60 }}>Tijdstip:</Typography>
                  <Typography><b>{moment(error.key).format("DD-MM HH:mm:ss")}</b></Typography>
                </div>
                <div style={{ display: "flex" }}>
                  <Typography sx={{ marginRight: theme.spacing(1), width: 60 }}>Functie:</Typography>
                  <Typography><b>{error.function}</b></Typography>
                </div>
                {error.type === "error" && <div style={{ display: "flex" }}>
                  <Typography sx={{ marginRight: theme.spacing(1), width: 60 }}>Contact:</Typography>
                  <Typography><a href={`mailto:ontwikkelaars@hkv.nl?subject=${packageJson.name} error&body=Applicatie: ${packageJson.name}%0D%0AMelding: ${error.message}%0D%0AFunctie: ${error.function}%0D%0ACode: ${error.number}`}><b>ontwikkelaars@hkv.nl</b></a></Typography>
                </div>}
              </div>
            ))}
          </div>
          <div className="dialog-actions">
            <Button
              variant="contained"
              color="primary"
              className="dialog-button"
              onClick={() => dispatch(clearError())}
            >
              OK
            </Button>
          </div>
        </DialogContent>
      </HKVDialog>
    )
  }

  const renderNotifications = () => {
    const notifications = errors.filter(error => error.type !== "critical")

    const height = 50
    return notifications.map((notification, index) => {
      const bottom = (height * index)

      return (
        <Snackbar
          disableWindowBlurListener
          key={`${notification.key}-${index}`}
          open={true}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          autoHideDuration={notification.duration ? notification.duration : 4000}
          onClose={(event, reason) => closeSnackbar(reason, index)}
          onClick={() => closeSnackbar("click", index)}
          sx={{
            zIndex: 99999999,
            width: "fit-content",
            maxWidth: `calc(100% - ${theme.spacing(2)})`,
            height: height,
            left: 8,
            right: 8,
            margin: "0 auto",
            bottom: `calc(${bottom}px + ${theme.spacing(1 * (index + 1))}) !important`
          }}
        >
          <Alert elevation={2} severity={notification.type}>
            <Typography className="ellipsis">
              {notification.message.toString()}
            </Typography>
          </Alert>
        </Snackbar>
      )
    })
  }

  const renderMenu = () => {
    return (
      <React.Fragment>
        <StyledMobileMenu>
          <IconButton
            sx={{ marginRight: theme.spacing(1), color: theme.palette.text.primary }}
            size="small"
            onClick={() => drawerStateChanged("left", true)}
          >
            <MenuRounded sx={{ fontSize: theme.typography.pxToRem(24) }} />
          </IconButton>
        </StyledMobileMenu>
        <DesktopMenu
          pages={pages.filter(p => !p.headMenu)}
        />
      </React.Fragment>
    )
  }

  const renderHeader = () => {
    return (
      <Header>
        <StyledHeadMenu>
          <ul className="menu-list">
            {pages.filter(p => p.headMenu).map((page, ix) => (
              <li
                key={ix}
                className="menu-item"
              >
                <HKVLink to={page.path} className="menu-link">
                  <Typography>{page.label}</Typography>
                </HKVLink>
              </li>
            ))}
          </ul>
        </StyledHeadMenu>
        <div className="partners">
          {partners.map((partner, ix) => (
            <HKVLink key={ix} to={partner.link}>
              <img
                src={partner.image}
                title={partner.title}
                alt={partner.alt}
                height={40}
                style={{ objectFit: "contain" }}
              />
            </HKVLink>
          ))}
        </div>
        <HKVLink id="rws" to="https://www.rijkswaterstaat.nl/">
          <img
            src="/static/images/logo-rws.svg"
            title="Naar de homepage van Rijkswaterstaat"
            alt="Rijkswaterstaat"
            width={270}
            height={88}
          />
        </HKVLink>
        <AppBar
          elevation={0}
          position="relative"
          sx={{
            background: theme.palette.geel.main,
            color: theme.palette.text.primary,
            flexDirection: "row",
            justifyContent: "center",
            height: 44
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
              maxWidth: 1200,
              margin: theme.spacing(0, 2)
            }}
          >
            {renderMenu()}
            <div style={{ display: "flex", alignItems: "center" }}>
              <TextField
                title="Zoeken"
                autoComplete="off"
                value={searchValue || ""}
                onChange={(event) => setSearchValue(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    navigate(`/zoeken?keyword=${searchValue}`)
                  }
                }}
                slotProps={{
                  input: {
                    sx: {
                      background: "white",
                      "> input": {
                        padding: theme.spacing(0.25, 1.5),
                        paddingRight: 0
                      }
                    },
                    endAdornment: <IconButton
                      title="Doorzoek de website"
                      size="small"
                      color="inherit"
                      onClick={() => navigate(`/zoeken?keyword=${searchValue}`)}
                    >
                      <SearchRounded />
                    </IconButton>
                  }
                }}
                placeholder="Zoeken"
                size="small"
              />
            </div>
          </div>
        </AppBar>
      </Header>
    )
  }

  const FooterBar = () => {
    const location = useLocation()
    const pathNames = location.pathname.split("/").filter(x => x)

    return (
      <AppBar
        elevation={1}
        position="relative"
        sx={{ display: "flex", flexDirection: "column", background: theme.palette.donkerblauw.main }}
      >
        {pathNames.length > 0 && <div className="site-footer-breadcrumb">
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <HKVLink to="/">
                <HomeRounded fontSize="small" sx={{ color: theme.palette.text.secondary }} />
              </HKVLink>
            </li>
            {pathNames.map((value, ix) => {
              const to = `/${pathNames.slice(0, ix + 1).join('/')}`
              const isLast = ix === pathNames.length - 1

              const label = capitalizeFirstLetter(value?.replaceAll("_", " "))

              return (
                <li key={ix} className="breadcrumb-item">
                  {isLast
                    ? <Typography component="span" variant="body2">{label}</Typography>
                    : <HKVLink
                      to={to}
                      sx={{
                        textDecoration: "underline",
                        "&:hover": {
                          textDecoration: "none"
                        }
                      }}
                    >
                      <Typography component="span" variant="body2">{label}</Typography>
                    </HKVLink>
                  }
                </li>
              )
            })}
          </ol>
        </div>}
        <div className="site-footer-content">
          <div style={{ display: "flex", alignItems: "center" }}>
            <HKVLink to="/privacyverklaring">
              <Typography component="span">Privacy</Typography>
            </HKVLink>
            <HKVLink to="https://www.rijkswaterstaat.nl/toegankelijkheid">
              <Typography component="span">Toegankelijkheid</Typography>
            </HKVLink>
          </div>
          <div style={{ display: "flex", alignItems: "center" }}>
            <Button
              title="Terug naar boven"
              variant="outlined"
              color="inherit"
              endIcon={<ArrowUpwardRounded />}
              onClick={() => {
                window.scrollTo({ top: 0, behavior: "smooth" })
              }}
              sx={{ "&:hover": { textDecoration: "underline" } }}
            >
              <Typography>Terug naar boven</Typography>
            </Button>
          </div>
        </div>
      </AppBar>
    )
  }

  const renderFooter = () => {
    return (
      <Footer>
        <a href="https://www.rijkswaterstaat.nl/" style={{ display: "block", margin: "auto", position: "relative" }}>
          <img
            className="site-footer-logo"
            src="/static/images/logo-footer.svg"
            alt="Rijkswaterstaat Ministerie van Infrastructuur en Waterstaat. Water. Wegen. Werken."
          />
        </a>
        <FooterBar />
      </Footer>
    )
  }

  const renderSubpages = (subpages) => {
    return subpages.map((sp, i) => {
      const key = sp.path.replaceAll("/", "_")
      const hasSubpages = sp.subpages?.length > 0

      return (
        <React.Fragment key={i}>
          <li className="menu-list-item">
            <HKVLink
              to={sp.path}
              onClick={(e) => {
                if (hasSubpages) {
                  e.preventDefault()
                  e.stopPropagation()
                  setOpenPages(prev => ({
                    ...prev,
                    [key]: !prev[key]
                  }))
                } else {
                  setOpenPages({})
                  drawerStateChanged("left", false)
                }
              }}
              sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: hasSubpages ? "100%" : "fit-content" }}
            >
              <Typography>{sp.label}</Typography>
              {hasSubpages && (
                <ChevronRightRounded
                  fontSize="small"
                  color="inherit"
                  className="transition"
                  sx={{ transform: openPages[key] ? "rotate(90deg)" : null }}
                />
              )}
            </HKVLink>
          </li>

          {hasSubpages && openPages[key] && (
            <ul style={{ paddingLeft: theme.spacing(4) }}>
              {renderSubpages(sp.subpages)}
            </ul>
          )}
        </React.Fragment>
      )
    })
  }

  return (
    <div className="App" id="app">
      {renderErrors()}
      {renderNotifications()}
      {renderHeader()}
      <HKVAnalytics />
      <Drawer
        anchor="left"
        open={drawerStates["left"]}
        onClose={(e) => drawerStateChanged("left", false)}
        slotProps={{
          paper: {
            sx: {
              width: "100%",
              maxWidth: 480,
              overflowX: "hidden",
              overflowY: "scroll"
            }
          }
        }}
      >
        <div>
          <div style={{ display: "flex", position: "relative" }}>
            <HKVLink
              to="https://www.rijkswaterstaat.nl/"
              sx={{
                width: 46,
                display: "block",
                margin: "0 auto",
                overflow: "hidden"
              }}
            >
              <img
                src="/static/images/logo-rws.svg"
                title="Naar de homepage van Rijkswaterstaat"
                alt="Rijkswaterstaat"
                width={270}
                height={88}
              />
            </HKVLink>
            <IconButton
              sx={{
                color: theme.palette.lintblauw.main,
                position: "absolute",
                top: theme.spacing(1),
                right: theme.spacing(1)
              }}
              onClick={() => drawerStateChanged("left", false)}
            >
              <CloseRounded />
            </IconButton>
          </div>
          <Menu>
            {pages.filter(p => !p.headMenu).map((page, ix) => {
              const key = page.path.replaceAll("/", "_")
              return (
                <React.Fragment key={ix}>
                  <li className="menu-list-item">
                    <HKVLink
                      to={page.path}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        width: "100%",
                        color: theme.palette.lintblauw.main
                      }}
                      onClick={(e) => {
                        if (page.subpages?.length > 0) {
                          e.preventDefault()
                          e.stopPropagation()
                          setOpenPages(prev => ({
                            ...prev,
                            [key]: !prev[key]
                          }))
                        } else {
                          setOpenPages({})
                          drawerStateChanged("left", false)
                        }
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: theme.spacing(2) }}>
                        <HKVIcon
                          iconName={page.icon}
                          style={{ fontSize: theme.typography.pxToRem(30) }}
                        />
                        <Typography
                          component="span"
                          sx={{ fontSize: theme.typography.pxToRem(19), fontWeight: 700 }}
                        >
                          {page.label}
                        </Typography>
                      </div>
                      {page.subpages?.length > 0 && (
                        <ChevronRightRounded
                          fontSize="large"
                          color="inherit"
                          className="transition"
                          sx={{
                            transform: openPages[key] ? "rotate(90deg)" : null
                          }}
                        />
                      )}
                    </HKVLink>
                  </li>

                  {page.subpages?.length > 0 && openPages[key] && (
                    <ul style={{ paddingLeft: theme.spacing(4) }}>
                      {renderSubpages(page.subpages)}
                    </ul>
                  )}
                </React.Fragment>
              )
            })}
          </Menu>
          {pages.filter(p => p.headMenu).length > 0 && <Menu
            style={{
              marginTop: theme.spacing(3),
              paddingTop: theme.spacing(2),
              borderTop: `1px solid ${theme.palette.grey[300]}`
            }}
          >
            {pages.filter(p => p.headMenu).map((page, ix) => (
              <li key={ix} className="menu-list-item">
                <HKVLink
                  to={page.path}
                  onClick={() => drawerStateChanged("left", false)}
                >
                  <Typography>{page.label}</Typography>
                </HKVLink>
              </li>
            ))}
          </Menu>}
          <div style={{ display: "flex", flexDirection: "column", gap: theme.spacing(2), margin: theme.spacing(0, 2, 2) }}>
            <Typography color="lintblauw" fontWeight={700} fontSize={20}>Partners</Typography>
            {partners.map((partner, ix) => (
              <HKVLink key={ix} to={partner.link}>
                <img
                  src={partner.image}
                  title={partner.title}
                  alt={partner.alt}
                  height={40}
                  style={{ objectFit: "contain" }}
                />
              </HKVLink>
            ))}
          </div>
        </div>
      </Drawer>
      <ScrollToTop />
      <Routes>
        <Route path="" element={<Home />} />
        <Route path="*" element={<NotFound />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/disclaimer" element={<Disclaimer />} />
        <Route path="/over-lvo" element={<OverLVO />} />
        <Route path="/privacyverklaring" element={<Privacyverklaring />} />
        <Route path="/themas/:thema?/:item?" element={<Thema categories={categories} />} />
        <Route path="/zoeken" element={<Zoeken onUnmount={() => setSearchValue()} />} />
      </Routes>
      {renderFooter()}
    </div>
  )
}

export default React.memo(App)